"use client";

import React, { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import type { Editor } from "@tiptap/core";
import type { Range } from "@tiptap/core";
import { setOpenEmbedModal, setCloseEmbedModal, setIsEmbedModalOpen, setGetEmbedModalRange } from "@/lib/embed-modal-helper";

interface EmbedModalContextValue {
  openEmbedModal: (
    editor: Editor,
    range: Range,
    position?: { top: number; left: number },
    provider?: string | null,
  ) => void;
  closeEmbedModal: () => void;
  isOpen: boolean;
  editor: Editor | null;
  range: Range | null;
  position: { top: number; left: number } | null;
  provider: string | null;
}

const EmbedModalContext = createContext<EmbedModalContextValue | undefined>(undefined);

export function EmbedModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [range, setRange] = useState<Range | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const [provider, setProvider] = useState<string | null>(null);

  const openEmbedModal = useCallback(
    (
      editorInstance: Editor,
      rangeInstance: Range,
      pos?: { top: number; left: number },
      providerId?: string | null,
    ) => {
      setEditor(editorInstance);
      setRange(rangeInstance);
      setPosition(pos || null);
      setProvider(providerId || null);
      setIsOpen(true);
    },
    []
  );

  const closeEmbedModal = useCallback(() => {
    setIsOpen(false);
    // Don't clear editor/range immediately to allow modal to use them on close
    setTimeout(() => {
      setEditor(null);
      setRange(null);
      setPosition(null);
      setProvider(null);
    }, 100);
  }, []);

  // Register modal functions globally so non-React code (headless extensions) can call them
  useEffect(() => {
    setOpenEmbedModal(openEmbedModal);
    setCloseEmbedModal(closeEmbedModal);
    setIsEmbedModalOpen(() => isOpen);
    setGetEmbedModalRange(() => range);
    return () => {
      setOpenEmbedModal(null);
      setCloseEmbedModal(null);
      setIsEmbedModalOpen(null);
      setGetEmbedModalRange(null);
    };
  }, [openEmbedModal, closeEmbedModal, isOpen, range]);

  return (
    <EmbedModalContext.Provider
      value={{
        openEmbedModal,
        closeEmbedModal,
        isOpen,
        editor,
        range,
        position,
        provider,
      }}
    >
      {children}
    </EmbedModalContext.Provider>
  );
}

export function useEmbedModalContext() {
  const context = useContext(EmbedModalContext);
  if (context === undefined) {
    throw new Error("useEmbedModalContext must be used within an EmbedModalProvider");
  }
  return context;
}

